import { useCallback, useEffect, useState } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import { AppSEO, NotFound } from "@hl/shared-features/lib/features/layout";
import {
  AppShell,
  Container,
  Title,
  Text,
  Stack,
  Grid,
  Badge,
  createStyles,
  Box,
  Group,
  Button,
  Center,
  Global,
  Space,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useNavigate, useParams } from "react-router-dom";

import { PortfolioItem, PortfolioStatus } from "../../apollo/graphql.generated";
import { ReactComponent as VerifiedIcon } from "../../assets/icons/verified-icon.svg";
import { PORTFOLIO_BASE_URL } from "../../config";
import { AppHeader } from "../layout/Header";

import { ItemDetailsOverlay } from "./ItemDetailsOverlay";
import { PortfolioNotFound } from "./PortfolioNotFound";
import { PortfolioTile } from "./PortfolioTile";
import { usePublicPortfolioQuery } from "./queries/portfolio.graphql.generated";

const GlobalStyles = () => (
  <Global
    styles={(theme) => ({
      ":root": {
        "--scrollbar-color": theme.colors.baseBackground[0],
        "--scrollbar-thumb-color": theme.colors.divider[0],
      },
      "*::-webkit-scrollbar": {
        // defined as css var to be able to change it at runtime
        // see `useHideScrollbar`
        backgroundColor: "var(--scrollbar-color)",
      },
      "::-webkit-scrollbar-thumb": {
        backgroundColor: "var(--scrollbar-thumb-color)",
      },
    })}
  />
);

const useStyles = createStyles({
  appShell: {
    height: "100%",
    overflow: "auto",
    position: "fixed",
    margin: -10,
    "&::-webkit-scrollbar": {
      width: 10,
    },
  },
  hlGradient: {
    background:
      "linear-gradient(90deg, #9B00F4 0%, #2E92ED 47.92%, #79E2F7 98.96%)",
  },
  hlMessage: {
    zIndex: 10,
    width: "100%",
    textAlign: "center",
    marginLeft: "15vw",
    color: "white",
    fontFamily: "'Aeonik Pro'",
    paddingRight: 10,
  },
  mobileMessage: {
    width: "100%",
    textAlign: "center",
    color: "white",
    fontFamily: "'Aeonik Pro'",
  },
  link: {
    color: "white",
    fontFamily: "'Aeonik Pro'",
    textDecoration: "none",
  },
  twitterLink: {
    color: "inherit",
    textDecoration: "none",
  },
});

export const PortfolioPublic = () => {
  const { slug, itemId } = useParams();
  const { classes, theme } = useStyles();
  const navigate = useNavigate();

  const { data, loading } = usePublicPortfolioQuery({
    variables: {
      slug: slug ?? "",
    },
    skip: !slug,
  });
  const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);

  const hideItem = useCallback(() => {
    setSelectedItem(null);
    navigate(`/@${slug}`, { replace: true });
  }, [setSelectedItem, navigate]);

  const enableCreatorPortfolio = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_CREATOR_PORTFOLIO
  );
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md}px)`);
  const portfolio = data?.publicPortfolio;
  useEffect(() => {
    if (portfolio) {
      const item = portfolio.items.edges.find((x) => x.id === itemId);
      setSelectedItem(item ?? null);
    }
  }, [portfolio, itemId, setSelectedItem]);

  if (!slug) {
    return <>Portfolio id is not provided</>;
  }

  if (loading || enableCreatorPortfolio === undefined) {
    return <LoadingPage />;
  }

  if (!enableCreatorPortfolio) {
    return <NotFound />;
  }

  const isCurated = !!portfolio?.curator;
  const hasTwitter = !!portfolio?.twitter;
  const twitterUrl = hasTwitter
    ? `https://twitter.com/${portfolio.twitter}`
    : "https://twitter.com/Highlight_xyz";

  if (!portfolio) {
    return <PortfolioNotFound />;
  }
  const managedByHl = (isMobile: boolean) => (
    <div className={isMobile ? classes.mobileMessage : classes.hlMessage}>
      This page is managed by Highlight. If you are the original artist, claim
      ownership by sending us an{" "}
      <a href="mailto:gm@highlight.xyz" className={classes.link}>
        email
      </a>
      .
    </div>
  );

  return (
    <>
      <GlobalStyles />
      <AppSEO
        title={`${portfolio.title ?? ""} - NFT Art Gallery`}
        description={portfolio.bio}
      >
        <link
          rel="canonical"
          href={`${PORTFOLIO_BASE_URL}/@${portfolio.slug}`}
        />
      </AppSEO>

      <AppShell
        className={classes.appShell}
        header={
          <>
            {!selectedItem && (
              <AppHeader
                className={
                  isCurated && !isMobile ? classes.hlGradient : undefined
                }
                centerContent={isCurated && !isMobile && managedByHl(isMobile)}
              />
            )}
            {isCurated && isMobile && !selectedItem && (
              <Box className={classes.hlGradient} mt={70} p="sm">
                {managedByHl(isMobile)}
              </Box>
            )}
          </>
        }
      >
        <Container size={1200}>
          {portfolio.status !== PortfolioStatus.LIVE && (
            <Badge color={STATUS_COLOR.ERROR} mb="sm">
              Portfolio is not public
            </Badge>
          )}
          <Stack spacing={12} align="center">
            {!isMobile && <Space h={"40px"} />}
            <Title size={32} align="center">
              {portfolio?.title}
            </Title>
            {
              <Stack spacing={0} align="center">
                {isCurated && (
                  <Center mb={8}>
                    <Group spacing="xs" maw={540}>
                      <Text color={TEXT_COLOR.SECONDARY} align="center">
                        <a
                          className={classes.twitterLink}
                          href="https://twitter.com/Highlight_xyz"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Curated by highlightxyz.eth
                        </a>
                      </Text>
                      <VerifiedIcon />
                    </Group>
                  </Center>
                )}
                <Text
                  sx={{ whiteSpace: "pre-line", overflow: "hidden" }}
                  align="center"
                  maw={"540px"}
                >
                  {portfolio.bio}
                </Text>
              </Stack>
            }

            {!isCurated && hasTwitter && (
              <Center>
                <Button
                  w={120}
                  color={SECONDARY_COLOR}
                  rightIcon={<VerifiedIcon />}
                  onClick={() => window.open(twitterUrl, "_blank")}
                >
                  Twitter
                </Button>
              </Center>
            )}
          </Stack>

          <Grid columns={12} gutter={40} pt={32}>
            {portfolio.items.edges.map((item) => (
              <Grid.Col xs={12} sm={6} md={4} key={item.id}>
                <PortfolioTile item={item} href={`/@${slug}/${item.id}`} />
              </Grid.Col>
            ))}
          </Grid>
        </Container>
        {selectedItem && (
          <ItemDetailsOverlay
            item={selectedItem}
            portfolio={portfolio}
            onClickOutside={hideItem}
          />
        )}
      </AppShell>
    </>
  );
};
